import { Select, Spin } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import ReactJson from "react-json-view";
import { useQuery } from "react-query";
import { AppContext } from "../AppContext";
import tr from "../i18n";

dayjs.extend(customParseFormat);
const Audit = (props) => {
  const { gardens, fetchAllGardens } = useContext(AppContext);

  useEffect(() => {
    console.count("Audit rendered");
    fetchAllGardens();
  }, [fetchAllGardens]);

  const ACTIONS_MAP = {
    "/api/device": {
      POST: { name: "Create device" },
      PUT: { name: "Update device" },
      DELETE: { name: "Delete device" },
    },
    "/api/device/location": {
      PUT: { name: "Update device location" },
    },
    "/api/garden": {
      POST: { name: "Create garden" },
      PUT: { name: "Update garden" },
      DELETE: { name: "Delete garden" },
    },
    "/api/garden/device": {
      PUT: { name: "Assign device" },
      DELETE: { name: "Remove device" },
    },
    "/api/garden/commit-schedules": {
      POST: { name: "Commit scheduler plans" },
    },
    "/api/garden/user": {
      PUT: { name: "Assign user" },
      DELETE: { name: "Unassign user" },
    },
    "/api/garden/optimize": {
      POST: { name: "Set Optimization" },
    },
    "/api/garden/tags": {
      PUT: { name: "Tag garden" },
      DELETE: { name: "Untag garden" },
    },
    "/api/garden/active-templates": {
      PUT: { name: "Activate template" },
    },
    "/api/plan": {
      POST: { name: "Create plan" },
      DELETE: { name: "Delete plan" },
    },
    "/api/plan/test/watering": {
      POST: { name: "Test watering" },
    },
    "/api/plan/manual": {
      POST: { name: "Manual watering" },
    },
    "/api/quick-plan-templates": {
      POST: { name: "Create quick plan template" },
      DELETE: { name: "Delete quick plan template" },
    },
    "/api/tag": {
      POST: { name: "Create tag" },
      DELETE: { name: "Delete tag" },
    },
    "/api/inventory": {
      GET: { name: "Inventory Query" },
    },
  };
  const auditDataRef = useRef([]);
  const [gardenId, setGardenId] = useState(null);
  const [auditData, setAuditData] = useState([]);
  const [tenantUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [gardenSearch, setGardenSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");

  const limit = 50;
  const scrollRef = useRef(null);

  // Fetch audit data with pagination
  const fetchAudit = async () => {
    console.log("Fetching audit for gardenId:", gardenId, "Page:", page);
    return axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/audit`, {
      params: { gardenId, userId: selectedUser, page, limit },
    });
  };

  const {
    data: auditRes,
    isLoading: isAuditLoading,
    refetch,
  } = useQuery(["GetAuditTrail", gardenId, selectedUser, page], fetchAudit, { enabled: gardenId !== undefined, keepPreviousData: false, refetchOnWindowFocus: false });

  useEffect(() => {
    console.count("Audit data effect");

    if (auditRes?.data?.success && auditRes?.data?.data) {
      if (page === 1) {
        // Reset only when we get the new data
        auditDataRef.current = auditRes.data.data;
      } else {
        // Append new data for pagination
        auditDataRef.current = [...auditDataRef.current, ...auditRes.data.data];
      }

      setAuditData([...auditDataRef.current]);

      if (auditRes.data.data.length < limit) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    }
    setIsFetchingMore(false);
  }, [auditRes?.data?.data, auditRes?.data?.success, page]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    refetch();
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [gardenId, refetch]);

  // Infinite scrolling: Fetch more data when reaching the bottom
  const handleScroll = useCallback(
    (event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && !isFetchingMore) {
        setIsFetchingMore(true);
        setPage((prev) => prev + 1);
      }
    },
    [hasMore, isFetchingMore]
  );

  const getAction = (url, method) => {
    const deleteGardenSubStr = ["/device", "/user", "/tags"];

    if (url.includes("devices-order")) return { name: "Order devices" };
    else if (url.includes("/api/garden") && !deleteGardenSubStr.some((ss) => url.includes(ss)) && method === "DELETE") {
      return { name: "Delete garden" };
    }

    if (url.includes("/api/device") && url.includes("mgmt") && method === "POST") {
      return { name: "Update Battery Mode" };
    }

    const correctedUrl = url.split("?")[0].replace(/\/$/, "");
    if (ACTIONS_MAP[correctedUrl] && ACTIONS_MAP[correctedUrl][method]) {
      return ACTIONS_MAP[correctedUrl][method];
    } else {
      return correctedUrl;
    }
  };

  const cronToString = (cron) => {
    if (!cron) return null;

    const parts = cron.split(" ");
    const hour = parts[1];
    const minute = parts[0];
    const days = parts[4];
    if (days === "*") return tr("Everyday");

    const daysOfWeek = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    const daysArr = days.split(",").map((d) => tr(daysOfWeek[d]));

    return `${hour}:${minute} (${daysArr})`;
  };

  const daysToString = (days) => {
    if (!days) return null;
    const daysOfWeek = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    const daysArr = days.split(",").map((d) => tr(daysOfWeek[d]));

    return `${daysArr}`;
  };

  const getComponent = (obj, name) => {
    switch (name) {
      case "Create device":
        return (
          <div>
            {obj.body && (
              <>
                <div className="flex flex-row w-full gap-5 my-2 p-4 border rounded-md">
                  <div className="flex flex-col">
                    {tr("Device Id")}: {obj.body?.id}
                  </div>
                  <div className="flex flex-col">
                    {tr("Device Name")}: {obj.body?.name}
                  </div>
                  <div className="flex flex-col">
                    {tr("Device Stock Id")}: {obj.body?.sid}
                  </div>
                  <div className="flex flex-col">
                    {tr("Device Model")}: {obj.body?.model}
                  </div>
                  <div className="flex flex-col">
                    {tr("Valve Count")}: {obj.body?.valveCount}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      case "Update device location":
        return (
          <div>
            {obj.query && (
              <>
                <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                  <div className="flex flex-col">
                    {tr("Device Serial")}: {obj.query?.deviceSerial}
                  </div>
                  <div className="flex flex-col">
                    {tr("Latitude")}: {obj.query?.lat}
                  </div>
                  <div className="flex flex-col">
                    {tr("Longitude")}: {obj.query?.lng}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      case "Delete device":
        return (
          <div>
            {obj.query && (
              <>
                <div className="flex flex-row w-full gap-5 my-2 p-4 border rounded-md">
                  <div className="flex flex-col">
                    {tr("Device Stock Id")}: {obj.query?.sid}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      case "Assign device":
        return (
          <div>
            {obj.body && (
              <>
                <div className="flex flex-row w-full gap-5 my-2 p-4 border rounded-md">
                  <div className="flex flex-col">
                    {tr("Garden Id")}: {obj.body?.gardenId}
                  </div>
                  {obj.body?.deviceIds && (
                    <div className="flex flex-col">
                      {tr("Device Id")}: {obj.body?.deviceIds.join(", ")}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      case "Remove device":
        return (
          <div>
            {obj.body && (
              <>
                <div className="flex flex-row w-full gap-5 my-2 p-4 border rounded-md">
                  <div className="flex flex-col">
                    {tr("Garden Id")}: {obj.body?.gardenId}
                  </div>
                  {obj.body?.deviceIds && (
                    <div className="flex flex-col">
                      {tr("Device Id")}: {obj.body?.deviceIds.join(", ")}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      case "Create plan":
        return (
          <div>
            {obj.body && (
              <>
                <div className="flex flex-row w-full gap-5 my-2 p-4 border rounded-md">
                  <div className="flex flex-col">
                    {tr("Device Serial")}: {obj.body?.deviceSerial}
                  </div>
                  <div className="flex flex-col">
                    {tr("Plan Name")}: {obj.body?.name}
                  </div>
                  <div className="flex flex-col">
                    {tr("Start Time")}: {cronToString(obj.body?.cron)}
                  </div>
                  <div className="flex flex-col">
                    {tr("Durations")}: [{obj.body?.durations.join(", ")}]
                  </div>
                </div>
              </>
            )}
          </div>
        );
      case "Delete plan":
        return (
          <div>
            {obj.query && (
              <>
                <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                  <div className="flex flex-col">
                    {tr("Device Serial")}: {obj.query?.deviceSerial}
                  </div>
                  <div className="flex flex-col">
                    {tr("Plan Id")}: {obj.query?.planId}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      case "Create garden":
        return (
          <div>
            {obj.body && (
              <>
                <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                  <div className="flex flex-col">
                    {tr("Garden Id")}: {obj.body?.id}
                  </div>
                  {obj.body?.name && (
                    <div className="flex flex-col">
                      {tr("Garden Name")}: {obj.body?.name}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      case "Delete garden":
        return (
          <div>
            {obj.params && (
              <>
                <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                  <div className="flex flex-col">
                    {tr("Garden Id")}: {obj.params?.id}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      case "Update garden":
        return (
          <div>
            {obj.body && (
              <>
                <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                  <div className="flex flex-col">
                    {tr("Garden Id")}: {obj.body?.id}
                  </div>
                  {obj.body?.name && (
                    <div className="flex flex-col">
                      {tr("Garden Name")}: {obj.body?.name}
                    </div>
                  )}
                  {obj.body?.wateringStatus && (
                    <div className="flex flex-col">
                      {tr("Watering Status")}: {obj.body?.wateringStatus}
                    </div>
                  )}
                  {obj.body?.schedulerMode && (
                    <div className="flex flex-col">
                      {tr("Scheduler Mode")}: {obj.body?.schedulerMode}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      case "Tag garden":
        return (
          <div>
            {obj.body && (
              <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                {obj.body?.tagIds && (
                  <div className="flex flex-col">
                    {tr("Tags")}: {obj.body?.tagIds.join(", ")}
                  </div>
                )}
                {obj.body?.gardenIds && (
                  <div className="flex flex-col">
                    {tr("Garden Id")}: {obj.body?.gardenIds.join(", ")}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      case "Assign user":
        return (
          <div>
            {obj.body && (
              <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                {obj.body?.userId && (
                  <div className="flex flex-col">
                    {tr("User Id")}: {obj.body?.userId}
                  </div>
                )}
                {obj.body?.gardenIds && (
                  <div className="flex flex-col">
                    {tr("Garden Id")}: {obj.body?.gardenIds.join(", ")}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      case "Set Optimization":
        return (
          <div>
            {obj.body && (
              <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                {obj.body?.gardenIds && (
                  <div className="flex flex-col">
                    {tr("Garden Id")}: {obj.body?.gardenIds.join(", ")}
                  </div>
                )}
                {obj.body?.optimize && (
                  <div className="flex flex-col">
                    {tr("Optimization")}: {JSON.stringify(obj.body?.optimize)}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      case "Order devices":
        return (
          <div>
            {obj.params && obj.body && (
              <>
                <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                  {obj.params.id && (
                    <div className="flex flex-col">
                      {tr("Garden Id")}: {obj.params?.id}
                    </div>
                  )}
                  {obj.body.deviceIds && (
                    <div className="flex flex-col">
                      {tr("Device Id")}: {obj.body?.deviceIds?.join(", ")}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      case "Inventory Query":
        return (
          <div>
            {obj.query && (
              <>
                <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                  {obj.query.serial && (
                    <div className="flex flex-col">
                      {tr("Device Serial")}: {obj.query?.serial}
                    </div>
                  )}
                  {obj.query.sid && (
                    <div className="flex flex-col">
                      {tr("Device Stock Id")}: {obj.query?.sid}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      case "Create quick plan template":
        return (
          <div>
            {obj.body && (
              <>
                <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                  {obj.body.startTime && (
                    <div className="flex flex-col">
                      {tr("Start Time")}: {obj.body?.startTime}
                    </div>
                  )}
                  {obj.body.duration && (
                    <div className="flex flex-col">
                      {tr("Duration")}: {obj.body?.duration} {tr("Mins")}
                    </div>
                  )}
                  {obj.body.activeDays && (
                    <div className="flex flex-col">
                      {tr("Active Days")}: {daysToString(obj.body?.activeDays.join(","))}
                    </div>
                  )}
                  {obj.body.gardenId && (
                    <div className="flex flex-col">
                      {tr("Garden Id")}: {obj.body?.gardenId}
                    </div>
                  )}
                  {obj.body.type && (
                    <div className="flex flex-col">
                      {tr("Type")}: {obj.body?.type}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      case "Commit scheduler plans":
        return (
          <div>
            {obj.body && (
              <>
                <div className="flex flex-col w-full gap-3 my-2 p-4 border rounded-md">
                  {obj.body.gardenId && (
                    <div className="flex flex-row">
                      <div className="flex flex-col">
                        {tr("Garden Id")}: {obj.body?.gardenId}
                      </div>
                    </div>
                  )}
                  {obj.body.commits != null && (
                    <>
                      {obj.body.commits.map((t, i) => (
                        <div className="flex flex-row gap-3">
                          {t.type === "create_plan" && (
                            <>
                              <div className="flex flex-col">
                                {tr("Type")}: {tr("Create plan")}
                              </div>
                              <div className="flex flex-col">
                                {tr("Device Serial")}: {t.data?.deviceSerial}
                              </div>
                              <div className="flex flex-col">
                                {tr("Plan Name")}: {t.data?.name}
                              </div>
                              <div className="flex flex-col">
                                {tr("Start Time")}: {cronToString(t.data?.cron)}
                              </div>
                              <div className="flex flex-col">
                                {tr("Durations")}: [{t.data?.durations.join(",")}]
                              </div>
                              <div className="flex flex-col">
                                {tr("Status")}: {t.data?.status}
                              </div>
                            </>
                          )}
                          {t.type === "delete_plan" && (
                            <>
                              <div className="flex flex-col">
                                {tr("Type")}: {tr("Delete plan")}
                              </div>
                              <div className="flex flex-row gap-3">
                                <div className="flex flex-col">
                                  {tr("Device Serial")}: {t.data?.deviceSerial}
                                </div>
                                <div className="flex flex-col">
                                  {tr("Plan Id")}: {t.data?.planId}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        );
      case "Activate template":
        return (
          <div>
            {obj.body && (
              <>
                <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                  {obj.body.gardenId && (
                    <div className="flex flex-col">
                      {tr("Garden Id")}: {obj.body?.gardenId}
                    </div>
                  )}
                  {obj.body.templateIds && (
                    <div className="flex flex-col">
                      {tr("Template Ids")}: {obj.body?.templateIds.join(", ")}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      case "Update Battery Mode":
        return (
          <div>
            {obj.params && obj.body && (
              <>
                <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                  {obj.body.mode && (
                    <div className="flex flex-col">
                      {tr("Battery Mode")}: {obj.body?.mode}
                    </div>
                  )}
                  {obj.params.sid && (
                    <div className="flex flex-col">
                      {tr("Device Stock Id")}: {obj.params.sid}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      case "Manual watering":
        return (
          <div>
            {obj.body && (
              <>
                <div className="flex flex-row w-full gap-3 my-2 p-4 border rounded-md">
                  {obj.body.deviceSerial && (
                    <div className="flex flex-col">
                      {tr("Device Serial")}: {obj.body?.deviceSerial}
                    </div>
                  )}
                  {obj.body.durations && (
                    <div className="flex flex-col">
                      {tr("Duration")}: {obj.body?.durations.join(",")}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      default:
        return <ReactJson collapsed src={obj} />;
    }
  };
  const normalize = (str) => str?.toString().toLowerCase().replace(/[çÇ]/g, "c").replace(/[ğĞ]/g, "g").replace(/[ıİ]/g, "i").replace(/[öÖ]/g, "o").replace(/[şŞ]/g, "s").replace(/[üÜ]/g, "u") || "";

  const filterOption = (input, option) => normalize(option?.children?.toString?.() || "").includes(normalize(input));

  return (
    <div className="bg-white border text-gray-800 rounded-md flex flex-row rounded-tl-none">
      <div className="flex flex-col mb-5 w-full">
        <div className="flex flex-row w-full justify-start gap-2 p-4">
          <div className="flex-1 min-w-0 max-w-xs">
            <Select
              showSearch
              value={gardenId ?? undefined}
              searchValue={gardenSearch}
              onSearch={(val) => setGardenSearch(val)}
              onChange={(val, option) => {
                setGardenId(val);
                setGardenSearch(option?.children?.toString?.() || "");
              }}
              optionFilterProp="children"
              className="w-full"
              placeholder={tr("All Gardens")}
              filterOption={filterOption}
            >
              <Select.Option value="">{tr("All Gardens")}</Select.Option>
              {gardens &&
                gardens.map((garden) => (
                  <Select.Option key={garden.id} value={garden.id}>
                    {garden.name}
                  </Select.Option>
                ))}
            </Select>
          </div>
          <div className="flex-1 min-w-0 max-w-xs">
            <Select
              showSearch
              value={selectedUser ?? undefined}
              searchValue={userSearch}
              onSearch={(val) => setUserSearch(val)}
              onChange={(val, option) => {
                setSelectedUser(val);
                setUserSearch(option?.children?.toString?.() || "");
              }}
              optionFilterProp="children"
              className="w-full"
              placeholder={tr("All Users")}
              filterOption={filterOption}
            >
              <Select.Option value="">{tr("All Users")}</Select.Option>
              {tenantUsers &&
                tenantUsers.map((user) => (
                  <Select.Option key={user.id} value={user.id}>
                    {user.name}
                  </Select.Option>
                ))}
            </Select>
          </div>
        </div>
        <Spin spinning={isAuditLoading} tip={tr("Loading")}>
          <div className="p-4 flex flex-col gap-2 h-full overflow-y-auto" onScroll={handleScroll} ref={scrollRef} style={{ maxHeight: "80vh" }}>
            {(auditData ?? []).map((a) => (
              <div key={a._id} className="flex flex-col">
                <div className="p-1 px-2 bg-gray-100 rounded-t-md text-xs flex gap-2 justify-between">
                  <div className="flex flex-col">
                    <div className=" text-sm">{tr(getAction(a.originalUrl, a.method).name)}</div>
                    <div className="text-gray-400">{dayjs(a.createdAt).format("DD/MM HH:mm:ss")}</div>
                  </div>
                  <div className=" flex flex-col items-end justify-center gap-1">
                    <div className="px-2 bg-blue-500 rounded-md text-xs text-white">{tr(a?.user?.role)} </div>
                    {a?.user?.name}
                  </div>
                </div>
                <div className="flex gap-2 border rounded-b-md p-1 px-2 flex-col">{getComponent(a, getAction(a.originalUrl, a.method).name)}</div>
              </div>
            ))}
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default Audit;
